/**
 * "Redefinir senha" da tela de usuários (spec 034) — a lógica pura, sem React,
 * para ser provada fora do navegador (o web não tem executor de testes; o teste
 * mora na API, apps/api/src/shared/password-reset-reveal.test.ts).
 *
 * A senha temporária chega UMA vez, na resposta do POST, e vive só no estado
 * do diálogo aberto:
 * - não passa pelo cache do react-query (a chamada é direta, fora de
 *   useQuery/useMutation, que a guardariam por `gcTime` depois de fechar);
 * - não vai para localStorage, URL nem log;
 * - fechar o diálogo (`closed`) devolve o estado inicial, sem a senha, e
 *   reabrir começa na confirmação — nunca mostra a senha anterior.
 */
import type { PasswordResetResponse } from '@erp/shared';

export type PasswordRevealState =
  | { step: 'confirm' }
  | { step: 'working' }
  | { step: 'shown'; secret: PasswordResetResponse }
  | { step: 'failed'; error: string; code?: string };

export type PasswordRevealAction =
  | { type: 'requested' }
  | { type: 'succeeded'; secret: PasswordResetResponse }
  | { type: 'failed'; error: string; code?: string }
  | { type: 'closed' };

export const INITIAL_REVEAL: PasswordRevealState = { step: 'confirm' };

export function revealReducer(
  state: PasswordRevealState,
  action: PasswordRevealAction
): PasswordRevealState {
  switch (action.type) {
    case 'requested':
      // um pedido por vez: o botão some no `working`, e a senha já mostrada não
      // é trocada por outra sem fechar (fechar = descartar)
      return state.step === 'confirm' || state.step === 'failed' ? { step: 'working' } : state;
    case 'succeeded':
      return state.step === 'working' ? { step: 'shown', secret: action.secret } : state;
    case 'failed':
      return state.step === 'working'
        ? { step: 'failed', error: action.error, ...(action.code ? { code: action.code } : {}) }
        : state;
    case 'closed':
      return INITIAL_REVEAL;
  }
}

/**
 * A pessoa da equipe sem conta no SSO (15/10): o espelho do TiFlux cria a pessoa no
 * ERP, mas não o login dela. O diálogo oferece criar o login (o "Novo usuário" com o
 * mesmo e-mail, que adota a pessoa, F-180) e segue para a senha temporária. Login de
 * portal (`client`) nasce por outro caminho, e não é oferecido aqui.
 */
export function offersLoginCreation(state: PasswordRevealState, person: { role: string }): boolean {
  return state.step === 'failed' && state.code === 'login_account_missing' && person.role !== 'client';
}

/** Códigos da API de redefinir senha → mensagem para o admin. */
export const PASSWORD_RESET_ERRORS: Record<string, string> = {
  forbidden: 'Só administradores redefinem senhas.',
  user_not_found: 'Usuário não encontrado — a lista pode estar desatualizada.',
  password_reset_self: 'A sua própria senha se troca em Minha conta.',
  user_inactive: 'Usuário inativo — ative-o antes de redefinir a senha.',
  user_in_other_tenant:
    'Esta pessoa também tem acesso a outra empresa. A senha dela não é trocada por aqui — fale com o suporte.',
  privileged_account:
    'Esta conta administra o login (SSO). A senha dela não é trocada por aqui — fale com o suporte.',
  login_account_missing:
    'Esta pessoa ainda não tem login no SSO: ela veio do TiFlux e nunca recebeu acesso ao ERP.',
  password_policy_rejected:
    'O SSO recusou a senha gerada. Tente de novo; se repetir, fale com o suporte.',
  // spec 041 #5: as sessões caem ANTES de a senha mudar — sem elas, a senha fica a de antes
  sessions_not_revoked:
    'As sessões abertas não puderam ser encerradas, e a senha não foi trocada. Tente de novo em instantes.',
  // spec 042 (F-140): a pessoa tem de estar na organização desta empresa no SSO
  organization_membership_missing:
    'Esta pessoa não está na organização desta empresa no SSO. A senha não foi trocada — fale com o suporte.',
  organization_check_unavailable:
    'Não foi possível confirmar no SSO que esta pessoa é desta empresa. A senha não foi trocada — fale com o suporte.',
  organization_join_failed:
    'O SSO não deixou incluir esta pessoa na organização desta empresa. A senha não foi trocada — fale com o suporte.',
  keycloak_unavailable: 'O SSO não respondeu. A senha não foi trocada — tente de novo em instantes.',
  impersonation_read_only: 'Sessão "ver como" é só leitura.',
};

export function passwordResetErrorMessage(code: string): string {
  return PASSWORD_RESET_ERRORS[code] ?? 'Não foi possível redefinir a senha. Tente de novo.';
}

/** Primeiro nome para a saudação ("Associação Lagoa" → "Associação"). */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

/**
 * A mensagem pronta para colar no WhatsApp: onde entrar, o e-mail, a senha
 * temporária e o que acontece no primeiro acesso. No modo keycloak a pessoa
 * cria a própria senha na primeira entrada (o realm obriga); no modo local não
 * há essa etapa, e a mensagem não promete o que não acontece.
 */
export function whatsappMessage(secret: PasswordResetResponse): string {
  const hello = firstName(secret.name);
  const lines = [
    hello ? `Olá, ${hello}!` : 'Olá!',
    '',
    `Seu acesso: ${secret.loginUrl}`,
    `E-mail: ${secret.email}`,
    `${secret.mustChangeOnLogin ? 'Senha temporária' : 'Senha'}: ${secret.temporaryPassword}`,
    '',
    secret.mustChangeOnLogin
      ? 'Você vai criar a sua senha no primeiro acesso.'
      : 'Depois de entrar, troque a senha em Minha conta.',
  ];
  return lines.join('\n');
}
